import firebase from "firebase/app";
import "firebase/functions";
import "firebase/firestore";
import { reactive } from "vue";
import { roleCollectionKey } from "@/entities/role";
import {
  evaluateTutorPasswordPolicy,
  schoolConfigPathOfTutorPath,
  tutorConfigPathOfTutorPath,
  tutorPasswordRequiredAtForProject,
  TutorPasswordStatus
} from "@/entities/tutor_password_policy";

/**
 * 先生のパスワード変更の状態。サーバーが先生の tutorConfigs に置いているので、ログイン直後に
 * 読む roles/{uid} → tutorConfigs を監視して自分で判定する（サーバーへの問い合わせは無い）。
 * スクール単位の無効はスーパー管理人が schoolConfigs に置くので、それも監視する。
 * ルーターの getRole と store の fetchTutorConfig・getSchoolDetail は同じ文書を読むが、動いている
 * 監視に相乗りするので追加の読み取りは無い
 */
export const tutorPasswordState = reactive({
  status: null as TutorPasswordStatus | null,
  ready: false,
  error: false
});

// 必須化の日時は環境ごとの定数。判定のロジックはどの環境も同じ
const requiredAt = tutorPasswordRequiredAtForProject(
  process.env.VUE_APP_FIREBASE_PROJECT_ID
);
// ログイン直後はトークンの反映が遅れて権限エラーになることがあるので、短く待って読み直す
const MAX_ATTEMPTS = 4;

type DocData = firebase.firestore.DocumentData;

let currentUid: string | null = null;
let stopRole: (() => void) | null = null;
let stopConfig: (() => void) | null = null;
let stopSchool: (() => void) | null = null;
/** 監視している tutorConfigs のパス。先生でなければ null */
let configPath: string | null = null;
/** 監視している schoolConfigs のパス。先生でなければ null */
let schoolPath: string | null = null;
/** undefined はまだ届いていない。null は文書が無い */
let config: DocData | null | undefined;
/** undefined はまだ届いていない。null は文書が無い（読めなかったときも null にして先生の項目だけで判定する） */
let school: DocData | null | undefined;
let settled = false;
let first: {
  promise: Promise<TutorPasswordStatus | null>;
  resolve: (status: TutorPasswordStatus | null) => void;
  reject: (error: unknown) => void;
} | null = null;
let deadlineTimer: ReturnType<typeof setTimeout> | null = null;

function evaluate(
  uid: string,
  data: DocData | null | undefined,
  schoolData: DocData | null | undefined,
  now = Date.now()
): TutorPasswordStatus {
  return {
    uid,
    ...evaluateTutorPasswordPolicy(
      data ?? undefined,
      now,
      requiredAt,
      schoolData ?? undefined
    )
  };
}

function settle(status: TutorPasswordStatus | null) {
  tutorPasswordState.status = status;
  tutorPasswordState.ready = true;
  tutorPasswordState.error = false;
  settled = true;
  first?.resolve(status);
  if (deadlineTimer) clearTimeout(deadlineTimer);
  deadlineTimer = null;
  // 期限の時刻になったら、読み直さずに今の時刻で判定し直す
  if (status?.showBanner)
    deadlineTimer = setTimeout(() => {
      if (
        currentUid === status.uid &&
        config !== undefined &&
        school !== undefined
      )
        settle(evaluate(status.uid, config, school));
    }, Math.min(Math.max(status.requiredAt - status.serverNow, 1), 2147483647));
}

/** tutorConfigs と schoolConfigs の両方が届いたら判定する */
function settleIfLoaded(uid: string) {
  if (config === undefined || school === undefined) return;
  settle(evaluate(uid, config, school));
}

function fail(error: unknown) {
  // 一度でも判定できていれば、その状態のまま使わせる（止めるのはルールとサーバーの役目）
  if (settled) {
    console.warn("tutor password status listener stopped", error);
    return;
  }
  tutorPasswordState.error = true;
  tutorPasswordState.ready = false;
  first?.reject(error);
}

function listenConfig(uid: string, path: string, attempt: number) {
  stopConfig = firebase
    .firestore()
    .doc(path)
    .onSnapshot(
      snapshot => {
        if (currentUid !== uid || configPath !== path) return;
        config = snapshot.exists ? snapshot.data() ?? null : null;
        settleIfLoaded(uid);
      },
      error => {
        if (currentUid !== uid || configPath !== path) return;
        stopConfig?.();
        stopConfig = null;
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (currentUid === uid && configPath === path && !stopConfig)
              listenConfig(uid, path, attempt + 1);
          }, 250 * attempt);
          return;
        }
        fail(error);
      }
    );
}

function listenSchool(uid: string, path: string, attempt: number) {
  stopSchool = firebase
    .firestore()
    .doc(path)
    .onSnapshot(
      snapshot => {
        if (currentUid !== uid || schoolPath !== path) return;
        school = snapshot.exists ? snapshot.data() ?? null : null;
        settleIfLoaded(uid);
      },
      error => {
        if (currentUid !== uid || schoolPath !== path) return;
        stopSchool?.();
        stopSchool = null;
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (currentUid === uid && schoolPath === path && !stopSchool)
              listenSchool(uid, path, attempt + 1);
          }, 250 * attempt);
          return;
        }
        // スクールの設定が読めなくても止めない。届いていなければ先生の項目だけで判定する
        // （スクール単位の無効を見ない従来の動き。止めるのはルールとサーバーの役目）
        console.warn("school password setting listener stopped", error);
        if (school === undefined) {
          school = null;
          settleIfLoaded(uid);
        }
      }
    );
}

function listenRole(uid: string, attempt: number) {
  stopRole = firebase
    .firestore()
    .collection(roleCollectionKey)
    .doc(uid)
    .onSnapshot(
      snapshot => {
        if (currentUid !== uid) return;
        const role = snapshot.exists ? snapshot.data() : null;
        const tutorPath: unknown = role?.ref?.path;
        const path =
          role?.type === "tutor" && typeof tutorPath === "string"
            ? tutorConfigPathOfTutorPath(tutorPath)
            : null;
        if (path === configPath) {
          // 所属が変わっていなければ、先生は tutorConfigs の監視が結果を出す
          if (path === null) settle(null);
          return;
        }
        stopConfig?.();
        stopConfig = null;
        stopSchool?.();
        stopSchool = null;
        configPath = path;
        schoolPath =
          path === null
            ? null
            : schoolConfigPathOfTutorPath(tutorPath as string);
        config = undefined;
        school = schoolPath === null ? null : undefined;
        if (path === null) settle(null);
        else {
          listenConfig(uid, path, 1);
          if (schoolPath !== null) listenSchool(uid, schoolPath, 1);
        }
      },
      error => {
        if (currentUid !== uid) return;
        stopRole?.();
        stopRole = null;
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (currentUid === uid && !stopRole) listenRole(uid, attempt + 1);
          }, 250 * attempt);
          return;
        }
        fail(error);
      }
    );
}

function stop() {
  stopConfig?.();
  stopConfig = null;
  stopSchool?.();
  stopSchool = null;
  stopRole?.();
  stopRole = null;
  configPath = null;
  schoolPath = null;
  config = undefined;
  school = undefined;
  settled = false;
  if (deadlineTimer) clearTimeout(deadlineTimer);
  deadlineTimer = null;
}

/**
 * 今のログイン状態に合わせて監視を始め、最初の判定を返す。
 * 監視が動いていれば直近の判定を返すだけで、読み取りは無い。確認できなかった後に呼ぶと張り直す
 */
export function refreshTutorPassword(): Promise<TutorPasswordStatus | null> {
  const uid = firebase.auth().currentUser?.uid || null;
  if (uid !== currentUid || (uid && !stopRole)) {
    stop();
    currentUid = uid;
    tutorPasswordState.status = null;
    tutorPasswordState.ready = false;
    tutorPasswordState.error = false;
    if (uid) {
      let resolve!: (status: TutorPasswordStatus | null) => void;
      let reject!: (error: unknown) => void;
      const promise = new Promise<TutorPasswordStatus | null>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      // 待つ人がいなくても未処理の reject にしない
      promise.catch(() => undefined);
      first = { promise, resolve, reject };
      listenRole(uid, 1);
    }
  }
  if (!uid) {
    tutorPasswordState.ready = true;
    tutorPasswordState.error = false;
    return Promise.resolve(null);
  }
  return settled ? Promise.resolve(tutorPasswordState.status) : first!.promise;
}

/** ログイン状態に追従して監視を始める。App.vue が起動時に 1 回呼ぶ */
export function observeTutorPassword(): () => void {
  const stopAuth = firebase.auth().onAuthStateChanged(() => {
    void refreshTutorPassword().catch(() => {
      /* The dedicated retry screen owns the error. */
    });
  });
  return () => {
    stopAuth();
    stop();
    currentUid = null;
  };
}
